import { ESdChangeReason, ICheckoutDto, IItemPlanDto, IUserDto } from "@kuika/kuika-cl-model";
import * as React from "react";
import { FireBaseService } from "../../../../firebase/firebase";
import { PaymentService } from "../../../../services/payment-service";
import { UserService } from "../../../../services/user-service";
import { IWithContextProp, withContext } from "../../../../shared/hoc/with-context";
import { IWithHistoryProp, withHistory } from "../../../../shared/hoc/with-history";
import { withManagement } from "../../../../shared/hoc/with-management";
import { KMainFunctions } from "../../../../shared/utility/main-functions";
import { SaveStatus } from "../../../../shared/utility/save-status";
import { KModal } from "../../../ui-kit/k-modal/k-modal";
import { KModalFooter } from "../../../ui-kit/k-modal/k-modal-footer";
import { type IKuikaButtonStatusLabels, type KuikaButtonStatus } from "../../../ui-kit/kuika-button/kuika-button";
import KuikaRadioGroup from "../../../ui-kit/kuika-radio-group/kuika-radio-group";
import KuikaH5 from "../../../ui-kit/typography/H5/H5";
import KuikaText from "../../../ui-kit/typography/Text/Text";
import KuikaTextBold from "../../../ui-kit/typography/TextBold/TextBold";
import KuikaTitle from "../../../ui-kit/typography/Title/Title";
import "./subscription-plans.scss";

declare let window: Window & { skipAlert: boolean; userInfo: IUserDto };

export interface ISubscriptionUpdateModalState {
  /** Where the payment step stands. Owned by `SaveStatus`, never set directly. */
  payStatus: KuikaButtonStatus;
  desiredSeatCount: number;
}

/** The commit hands over to the payment provider rather than saving. */
const PAY_STATUS_LABELS: IKuikaButtonStatusLabels = { loading: "Processing", success: "Done" };

export interface ISubscriptionUpdateModalProps {
  showModal?: boolean;
  closeModal: () => void;
  nextSubscription: any;
  desiredSubscription: IItemPlanDto;
}

class SubscriptionUpdate extends React.PureComponent<
  ISubscriptionUpdateModalProps & IWithContextProp & IWithHistoryProp,
  ISubscriptionUpdateModalState
> {
  private readonly payStatus: SaveStatus = new SaveStatus((payStatus: KuikaButtonStatus): void => {
    this.setState({ payStatus });
  });

  constructor(props: ISubscriptionUpdateModalProps & IWithContextProp & IWithHistoryProp) {
    super(props);
    this.state = {
      payStatus: "idle",
      desiredSeatCount: 1
    };
  }

  componentWillUnmount(): void {
    this.payStatus.dispose();
  }

  gotoChangePlan = async (id: string, changePlan: boolean, nextId: string | undefined) => {
    const firebase = new FireBaseService();
    const obj = {} as ICheckoutDto;
    const baseUrl = `${window.location.origin}/${(window as any).currentDomain}/settings/subscriptionPlans`;
    const failUrl = `${baseUrl}?status=failedSubscriptionCheckout`;
    const successUrl = `${baseUrl}?status=successSubscriptionCheckout`;
    const cancelUrl = `${baseUrl}?status=cancelSubscription`;
    obj.FailUrl = failUrl;
    obj.SuccessUrl = successUrl;
    obj.PriceId = nextId || id;
    obj.DesiredSeatCount = 1;
    if (!id) {
      firebase.logEvent("cancel_subscription", {
        price_id: id,
        change_plan: changePlan
      });
      const res = await (await PaymentService.CancelSubscription()).data;
      // The ✓ goes up first; the dialog leaves and the page moves on after it.
      this.payStatus.succeed((): void => {
        this.props.closeModal();
        window.location.replace(cancelUrl);
      });
    } else if (changePlan) {
      firebase.logEvent("update_subscription", {
        price_id: id,
        change_plan: changePlan
      });
      const res = await (await PaymentService.UpdateSubscription(obj)).data;
      if (res.error) {
        KMainFunctions.displayErrorNotification(res.error);
        this.payStatus.fail();
      } else {
        firebase.logEvent("new_subscription", {
          price_id: id,
          change_plan: changePlan
        });
        // A plan change is billing: its notice stays a toast. The ✓ is held before the dialog
        // leaves, well inside the three seconds before the page reloads onto the new plan.
        this.payStatus.succeed((): void => this.props.closeModal());
        KMainFunctions.displayWarningNotification(
          "If your new subscription has lesser seats then your current user count you have to delete users to match your avaliable seats, or your users may not be able to login."
        );
        setTimeout(() => {
          window.skipAlert = true;
          window.location.replace(`${successUrl}&change_plan=true&price_id=${id}`);
        }, 3000);
      }
    } else {
      const res = await (await PaymentService.Checkout(obj)).data;
      // The ✓ goes up first; the dialog leaves and the checkout opens after it.
      this.payStatus.succeed((): void => {
        this.props.closeModal();
        if (res != undefined) window.location.replace(res);
      });
    }

    await UserService.GetUserInfo();
  };

  onSave = async () => {
    // A second press while the payment step runs or its ✓ is held would start it twice.
    if (!this.payStatus.canStart) return;
    const currentPlanId = window?.userInfo?.currentPlanId || "";
    this.payStatus.start();
    window.skipAlert = true;
    try {
      await this.gotoChangePlan(
        this.props.desiredSubscription.stripeId,
        !!currentPlanId,
        this.props.nextSubscription ? this.props.nextSubscription.id : undefined
      );
    } catch {
      // Before, a failed request left the button spinning for good; the dialog stays and says so.
      if (this.payStatus.current === "loading") this.payStatus.fail();
    }
  };

  /** GO BACK, the X and Escape. During the ✓ hold the dialog is already on its way out. */
  handleClose = (): void => {
    if (this.payStatus.current === "success") return;
    this.props.closeModal();
  };

  convertPriceCurrency = (currency) => {
    currency = currency?.toLowerCase();
    if (currency == "eur") {
      currency = "€";
    } else if (currency == "usd") {
      currency = "$";
    } else if (currency == "tl") {
      currency = "₺";
    } else {
      currency = "";
    }
    return currency;
  };

  componentDidMount(): void {
    const firebase = new FireBaseService();
    firebase.logEvent("subscription_update_page_loaded");
  }

  render() {
    const rawPrice = Number(this.props.desiredSubscription?.planPrice ?? 0);
    const priceStr = `$${rawPrice}`;
    const priceStrUsd = `$${rawPrice}`;
    const priceStrFixed = `$${rawPrice.toFixed(2)}`;
    const nextMonthDate = (() => {
      const now = new Date();
      const d = new Date(now.getFullYear(), now.getMonth() + 1, now.getDate());
      return d;
    })();
    const getOrdinalSuffix = (n: number) => {
      const s = ["th", "st", "nd", "rd"];
      const v = n % 100;
      return s[(v - 20) % 10] || s[v] || s[0];
    };
    const dayWithSuffix = `${nextMonthDate.getDate()}${getOrdinalSuffix(nextMonthDate.getDate())}`;

    return (
      <KModal
        width={340}
        showModal={this.props.showModal}
        closeModal={this.handleClose}
        header="ORDEr"
        isLoading={this.state.payStatus === "loading"}
        footer={
          <KModalFooter
            onCancel={this.handleClose}
            cancelLabel="Go back"
            submitLabel={`Pay $${rawPrice} now`}
            onSubmit={this.onSave}
            status={this.state.payStatus}
            statusLabels={PAY_STATUS_LABELS}
          />
        }
      >
        <div className="subscription-update-modal">
          <div className="subscription-update-modal__price">
            <div className="subscription-update-modal__price-header">
              <KuikaRadioGroup options={[{ label: "Monthly", value: "monthly" }]} />
            </div>
            <div className="subscription-update-modal__price-content">
              <div className="subscription-update-modal__price-line">
                <KuikaTextBold className="subscription-update-modal__price-value">{priceStr}</KuikaTextBold>
                <KuikaText className="subscription-update-modal__price-unit">/mo</KuikaText>
              </div>
              <KuikaText className="subscription-update-modal__price-period">{`${priceStr} billed monthly`}</KuikaText>
            </div>
          </div>

          <div className="subscription-update-modal__row">
            <KuikaTitle>{`Plan: ${this.props.desiredSubscription?.planName ?? ""}`}</KuikaTitle>
            <KuikaTitle>{`$+${rawPrice}`}</KuikaTitle>
          </div>

          <div className="subscription-update-modal__row">
            <KuikaTitle>Total due today</KuikaTitle>
            <KuikaH5>{priceStrUsd}</KuikaH5>
          </div>

          <KuikaText className="subscription-update-modal__monthly-text">
            {`${priceStrFixed} + taxes (minus credits) will be billed monthly on the ${dayWithSuffix}, unless canceled.`}
          </KuikaText>
        </div>
      </KModal>
    );
  }

  checkNeedRender = (changeReason: ESdChangeReason): boolean => {
    return false;
  };
}

const SubscriptionUpdateModal = withContext<ISubscriptionUpdateModalProps>(
  withManagement<ISubscriptionUpdateModalProps>(withHistory<ISubscriptionUpdateModalProps>(SubscriptionUpdate))
);
export { SubscriptionUpdateModal };
